import React, { useState } from 'react';
import { Button, Checkbox, FormControlLabel, Grid, TextField } from '@material-ui/core';

import ProductField from '../fields/ProductField';
import PriceField, { cleanPrice } from '../fields/PriceField';
import DateField from '../fields/DateField';

const validateForm = ({ product, price, isCustomProduct, customProduct }) => {
  let errors = {};

  if(!product && !isCustomProduct) {
    errors = {
      ...errors,
      product: 'Selecione um produto'
    }
  }

  if(!customProduct && isCustomProduct) {
    errors = {
      ...errors,
      customProduct: 'Digite o nome do produto customizado'
    }
  }

  if(!price || +price < 1) {
    errors = {
      ...errors,
      price: 'Preço precisa ser maior que R$ 1,00'
    }
  }

  return {
    hasError: Object.values(errors).length > 0,
    errors
  }
}

export default function PaymentForms({ defaultPayment, onSubmitPayment }) {
  const [product, setProduct] = useState(defaultPayment?.product || null);
  const [price, setPrice] = useState(defaultPayment?.price || '');
  const [dueAt, setDueAt] = useState(defaultPayment?.dueAt || '');
  const [instructions, setInstructions] = useState(defaultPayment?.instructions || '');
  const [notes, setNotes] = useState(defaultPayment?.notes || '');
  const [needInstallments, setNeedInstallments] = useState(defaultPayment?.needInstallments || false);
  const [installments, setInstallments] = useState(defaultPayment?.installments || 1);
  const [installmentPrice, setInstallmentPrice] = useState(defaultPayment?.installmentPrice || '');
  const [formError, setFormError] = useState({
    product: '',
    price: '',
    customProduct: ''
  });
  const [isCustomProduct, setIsCustomProduct] = useState(defaultPayment?.product?.isCustomProduct || false);
  const [customProduct, setCustomProduct] = useState(defaultPayment?.product?.name || '');

  const hasError = Object.values(formError).some(Boolean);

  const resetFormError = () => {
    if(hasError) {
      setFormError({
        product: '',
        price: '',
        customProduct: ''
      })
    }
  }

  const clearForms = () => {
    setProduct(null);
    setPrice('');
    setInstallments(1);
    setInstallmentPrice('');
    setNeedInstallments(false);
    setInstructions('');
    setNotes('')
  }

  const handleChangeProduct = (product) => {
    resetFormError();

    setProduct(product);
    setPrice(Number(product?.price).toFixed(2));
    setInstallments(1);
    setInstallmentPrice(Number(product?.price).toFixed(2));
    setNeedInstallments(false);
    setInstructions('');
    setNotes('')
  }

  const handleChangePrice = (value) => {
    resetFormError();
    
    setPrice(value);
    setInstallmentPrice(+cleanPrice(value || '') / +installments);
  }

  const handleChangeNeedInstallments = (checked) => {
    setNeedInstallments(checked);
  }

  const handleChangeInstallments = (e) => {
    const currentInstallments = +e.target.value;
    
    setInstallments(currentInstallments);
    setInstallmentPrice(+cleanPrice(price || '') / +currentInstallments);
  }

  const handleChangeInstallmentPrice = (price) => {
    setInstallmentPrice(price);
    setPrice(+cleanPrice(price || '') * +installments);
  }

  const handleSubmit = (e) => {
    e?.preventDefault();

    const validateError = validateForm({ product, price, isCustomProduct, customProduct });

    if(validateError.hasError) {
      setFormError(validateError.errors);
      return;
    }

    const normalizedPrice = Number.isNaN(+price)
      ? cleanPrice(price)
      : +price;

    const normalizedInstallmentPrice = Number.isNaN(+installmentPrice)
      ? cleanPrice(installmentPrice)
      : +installmentPrice;

    onSubmitPayment({
      product: {
        id: product?.id,
        name: isCustomProduct ? customProduct : product?.name,
        isCustomProduct
      },
      price: Number(normalizedPrice)?.toFixed(2),
      dueAt,
      instructions,
      notes,
      needInstallments,
      installments,
      installmentPrice: Number(normalizedInstallmentPrice)?.toFixed(2)
    });
  }

  const toggleCustomBoleto = () => {
    setIsCustomProduct(prev => !prev);
    clearForms();
  }

  const handleCustomProduct = (e) => {
    resetFormError();

    setCustomProduct(e.target.value);
  }

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={3} direction="column">
        <Grid item container spacing={1} direction="column">
          <Grid item>
            <FormControlLabel
              label="Boleto customizado?"
              control={
                <Checkbox
                  color="primary" 
                  checked={isCustomProduct}
                  onChange={toggleCustomBoleto}
                />
              }
            />
          </Grid>
          {!isCustomProduct ? (
            <Grid item>
              <ProductField
                autoFocus
                selectedProduct={product}
                onChangeProduct={handleChangeProduct}
                error={!!formError.product}
                helperText={formError.product}
              />
            </Grid>
          ) : (
            <TextField
              label="Produto customizado"
              value={customProduct}
              onChange={handleCustomProduct}
              error={!!formError.customProduct}
              helperText={formError.customProduct}
            />
          )}
        </Grid>
        {(isCustomProduct || !!product) && (
          <>
            <Grid item>
              <PriceField
                price={price}
                onChangePrice={handleChangePrice}
                error={!!formError.price}
                helperText={formError.price}
              />
            </Grid>
            <Grid item container direction="column" spacing={3}>
              <Grid item>
                <FormControlLabel
                  label="Parcelar Boleto?"
                  control={
                    <Checkbox
                      color="primary" 
                      checked={needInstallments}
                      onChange={(e) => handleChangeNeedInstallments(e.target.checked)}
                    />
                  }
                />
              </Grid>
              {needInstallments && (
                <>
                  <Grid item>
                    <TextField
                      label="Número parcelas"
                      type="number"
                      inputProps={{
                        min: 1,
                        max: 18,
                      }}
                      value={installments}
                      onChange={handleChangeInstallments}
                      helperText="Parcelas entre 1 e 18 parcelas, contatar TI se precisar de mais"
                    />
                  </Grid>
                  <Grid item>
                    <PriceField
                      label="Valor parcela"
                      price={installmentPrice}
                      onChangePrice={handleChangeInstallmentPrice}
                      // error={!!formError.price}
                      // helperText={formError.price}
                    />
                  </Grid>
                </>
              )}
            </Grid>
            <Grid item>
              <DateField
                label="Vencimento (Opcional)"
                type="date"
                value={dueAt}
                onChangeDate={(date) => setDueAt(date)}
                min={new Date().toISOString().slice(0, 10)}
                helperText="Vencimento padrão de 3 dias (não pode ser anterior a hoje)"
                InputLabelProps={{
                  shrink: true
                }}
              />
            </Grid>
            <Grid item>
              <TextField
                fullWidth
                label="Instruções Boleto (Opcional)"
                value={instructions}
                onChange={e => setInstructions(e.target.value)}
                helperText="Instruções aparecem nos boletos gerados"
              />
            </Grid>
            <Grid item>
              <TextField
                fullWidth
                label="Observações (Opcional)"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                helperText="Obervações não aparecem nos boletos gerados"
              />
            </Grid>
          </>
        )}
        <Grid item container justifyContent="flex-end">
          <Grid item>
            <Button variant="contained" color="primary" type="submit">Salvar</Button>
          </Grid>
        </Grid>
      </Grid>
    </form>
  )
}